import { findAssociatedTokenPda, TOKEN_PROGRAM_ADDRESS } from '@solana-program/token';
import { address as parseSolanaAddress } from '@solana/kit';
import { useQuery } from '@tanstack/react-query';

import { SOL_ADDRESS_SOLANA } from '@/constants/addresses';

import { isSolanaAddress } from '@/utils/models';
import { getSolanaRpc, lamportToSol } from '@/utils/solana';

/** Normalized token balance shape, mirroring the kit `getTokenAccountBalance` value. */
export type SolanaTokenAmount = {
  amount: string;
  decimals: number;
  uiAmount: number | null;
  uiAmountString: string;
};

export const splTokenBalanceQueryKey = (
  tokenAddress: string | undefined,
  accountAddress: string | undefined,
) => ['spl-token-balance', tokenAddress, accountAddress] as const;

export function useSplTokenBalance({
  tokenAddress,
  accountAddress,
  refetchInterval,
}: {
  tokenAddress: string | undefined;
  accountAddress: string | undefined;
  refetchInterval?: number;
}) {
  return useQuery({
    queryKey: splTokenBalanceQueryKey(tokenAddress, accountAddress),
    queryFn: async (): Promise<SolanaTokenAmount | undefined> => {
      const rpc = getSolanaRpc();
      const owner = parseSolanaAddress(accountAddress!);

      if (tokenAddress === SOL_ADDRESS_SOLANA) {
        // kit returns lamports as a bigint; route through the Decimal helper for the UI amount.
        const { value: lamports } = await rpc.getBalance(owner, { commitment: 'confirmed' }).send();
        const uiAmountString = lamportToSol(lamports.toString());

        return {
          amount: lamports.toString(),
          decimals: 9,
          uiAmount: Number(uiAmountString),
          uiAmountString,
        };
      }

      const mint = parseSolanaAddress(tokenAddress!);
      const [associatedTokenAddress] = await findAssociatedTokenPda({
        owner,
        mint,
        tokenProgram: TOKEN_PROGRAM_ADDRESS,
      });

      try {
        const { value } = await rpc
          .getTokenAccountBalance(associatedTokenAddress, { commitment: 'confirmed' })
          .send();
        return value;
      } catch {
        // If the standard ATA doesn't exist, search for any token accounts
        const { value: tokenAccounts } = await rpc
          .getTokenAccountsByOwner(owner, { mint }, { encoding: 'jsonParsed' })
          .send();

        if (tokenAccounts.length > 0) {
          // Use the first account found
          return tokenAccounts[0].account.data.parsed.info.tokenAmount;
        }

        return undefined;
      }
    },
    enabled:
      !!accountAddress &&
      !!tokenAddress &&
      isSolanaAddress(accountAddress) &&
      isSolanaAddress(tokenAddress),
    refetchInterval,
  });
}
